import React, { useEffect, useState } from "react";
import { Row, Col, Button, Form, Alert, Badge, Table } from "react-bootstrap";
import { getLedger, getEarningsSummary, requestSettlement, listSettlements } from "../services/partnerAuthService";

const getErr = (err, fallback) => err.response?.data?.error || fallback;
const SETTLEMENT_TONE = { Pending: "warning", Approved: "info", Paid: "success", Rejected: "danger" };
const LEDGER_TONE = { BookingEarning: "success", Settlement: "secondary", Adjustment: "info", Refund: "danger" };

export default function Earnings() {
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [requesting, setRequesting] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([getEarningsSummary(), getLedger(), listSettlements()])
      .then(([summaryRes, ledgerRes, settlementsRes]) => {
        setSummary(summaryRes.data.data);
        setLedger(ledgerRes.data.data || []);
        setSettlements(settlementsRes.data.data || []);
      })
      .catch((err) => setError(getErr(err, "Failed to load earnings")))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleRequestSettlement = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    const value = Number(amount);
    if (!value || value <= 0) return setError("Enter a valid amount");

    setRequesting(true);
    try {
      await requestSettlement(value);
      setSuccess("Settlement requested");
      setAmount("");
      load();
    } catch (err) {
      setError(getErr(err, "Unable to request settlement"));
    } finally {
      setRequesting(false);
    }
  };

  if (loading) return <p>Loading earnings…</p>;

  return (
    <div>
      <h4 className="mb-3">Earnings &amp; Settlements</h4>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Row className="g-3 mb-4">
        <Col md={3}>
          <div className="partner-stat-card">
            <div className="partner-stat-value">₹{summary?.totalEarnings || 0}</div>
            <div className="partner-stat-label">Total Earnings</div>
          </div>
        </Col>
        <Col md={3}>
          <div className="partner-stat-card">
            <div className="partner-stat-value">₹{summary?.settled || 0}</div>
            <div className="partner-stat-label">Settled</div>
          </div>
        </Col>
        <Col md={3}>
          <div className="partner-stat-card">
            <div className="partner-stat-value">₹{summary?.pendingRequests || 0}</div>
            <div className="partner-stat-label">Requested (Unpaid)</div>
          </div>
        </Col>
        <Col md={3}>
          <div className="partner-stat-card">
            <div className="partner-stat-value">₹{summary?.availableForSettlement || 0}</div>
            <div className="partner-stat-label">Available to Request</div>
          </div>
        </Col>
      </Row>

      <div className="partner-card mb-4">
        <h6 className="mb-3">Request a Settlement</h6>
        <Form onSubmit={handleRequestSettlement} className="d-flex gap-2 flex-wrap align-items-start">
          <Form.Control
            type="number"
            placeholder="Amount ₹"
            style={{ maxWidth: 200 }}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <Button type="submit" disabled={requesting}>{requesting ? "Requesting…" : "Request Settlement"}</Button>
        </Form>
        <small className="text-muted d-block mt-2">
          Bank details on your Business Profile are used for payout. Amount must not exceed the available balance above.
        </small>
      </div>

      <div className="partner-card mb-4">
        <h6 className="mb-3">Settlement History</h6>
        {settlements.length === 0 ? (
          <p className="text-muted mb-0">No settlement requests yet.</p>
        ) : (
          <div className="table-responsive">
            <Table size="sm" borderless className="align-middle mb-0">
              <tbody>
                {settlements.map((s) => (
                  <tr key={s._id}>
                    <td>{new Date(s.createdAt).toLocaleDateString()}</td>
                    <td>₹{s.amount}</td>
                    <td><Badge bg={SETTLEMENT_TONE[s.status] || "secondary"}>{s.status}</Badge></td>
                    <td className="text-muted small">{s.paymentReference || s.adminNote || ""}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      <div className="partner-card">
        <h6 className="mb-3">Ledger</h6>
        {ledger.length === 0 ? (
          <p className="text-muted mb-0">No ledger entries yet.</p>
        ) : (
          <div className="table-responsive">
            <Table size="sm" borderless className="align-middle mb-0">
              <tbody>
                {ledger.map((entry) => (
                  <tr key={entry._id}>
                    <td>{new Date(entry.createdAt).toLocaleDateString()}</td>
                    <td><Badge bg={LEDGER_TONE[entry.type] || "secondary"}>{entry.type}</Badge></td>
                    <td className={entry.amount < 0 ? "text-danger" : "text-success"}>
                      {entry.amount < 0 ? "-" : "+"}₹{Math.abs(entry.amount)}
                    </td>
                    <td className="text-muted small">{entry.note}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
